import { useState } from 'react';
import type { Recommendation } from '@lizrd/contracts';
import { cn } from '../lib/utils';

export type LifecycleAction = 'accept' | 'reject' | 'complete' | 'reopen';

/** How each action looks as the primary button and in the chevron menu. */
const ACTION_META: Record<
  LifecycleAction,
  { label: string; menuLabel: string; tone: 'advance' | 'neutral' | 'danger' }
> = {
  accept: { label: 'Accept', menuLabel: '✓ Accept', tone: 'advance' },
  complete: { label: 'Mark executed', menuLabel: '✓ Mark executed', tone: 'advance' },
  reopen: { label: 'Reopen', menuLabel: '↩ Reopen', tone: 'neutral' },
  reject: { label: 'Reject', menuLabel: '✕ Reject', tone: 'danger' },
};

/** The state machine: for each status, its badge + the primary "next" transition
 * and any other allowed transitions (shown under the chevron). */
const STATE_META: Record<
  string,
  { label: string; color: string; primary?: LifecycleAction; menu: LifecycleAction[] }
> = {
  open: { label: 'Open', color: 'var(--muted)', primary: 'accept', menu: ['reject'] },
  accepted: {
    label: 'Accepted',
    color: 'var(--iris)',
    primary: 'complete',
    menu: ['reopen', 'reject'],
  },
  completed: {
    label: 'Awaiting Validation',
    color: 'var(--viridian)',
    primary: 'reopen',
    menu: [],
  },
  validated: { label: 'Validated', color: 'var(--iris-strong)', primary: 'reopen', menu: [] },
  failed_validation: {
    label: 'Needs Attention',
    color: 'var(--warning)',
    primary: 'complete',
    menu: ['reopen'],
  },
  rejected: { label: 'Rejected', color: 'var(--danger)', primary: 'reopen', menu: ['accept'] },
  superseded: { label: 'Superseded', color: 'var(--muted)', menu: [] },
};

const TONE_CLASS = {
  advance: 'bg-iris text-on-iris hover:bg-iris-strong',
  neutral: 'bg-surface-2 text-bone hover:bg-surface-2/70',
  danger: 'bg-surface-2 text-danger hover:bg-danger-dim',
} as const;

/**
 * The single, reusable control for moving a recommendation through its workflow.
 * A non-clickable badge shows the current state; the primary button performs the
 * typical next transition (e.g. Open → Accept); a chevron reveals any other
 * allowed transitions. Used on the list cards (`sm`) and the guidance modal
 * footer (`lg`, full-width). Reject is surfaced here but the caller decides
 * whether to confirm it.
 */
export function RecommendationLifecycle({
  status,
  pending,
  onAction,
  size = 'sm',
  readOnly = false,
  menuPlacement = 'bottom',
  className,
}: {
  status: Recommendation['status'];
  pending: boolean;
  onAction: (action: LifecycleAction) => void;
  size?: 'sm' | 'lg';
  /** Render only the state badge, no transition controls (e.g. for viewers). */
  readOnly?: boolean;
  /** Which way the "other transitions" menu opens — use `top` where the control
   * sits at the bottom of a clipped container (e.g. the modal footer). */
  menuPlacement?: 'top' | 'bottom';
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const meta = STATE_META[status] ?? { label: status, color: 'var(--muted)', menu: [] };
  const lg = size === 'lg';
  const pad = lg ? 'px-4 py-2.5 text-sm' : 'px-3 py-1.5 text-xs';
  const showActions = !readOnly && !!meta.primary;

  // Read-only (or terminal-with-no-transitions) → a lone state badge.
  if (!showActions) {
    return (
      <span
        className={cn(
          'inline-flex items-center rounded-md border border-hairline font-semibold uppercase tracking-wide',
          lg ? 'px-4 py-2.5 text-xs' : 'px-2.5 py-1.5 text-[0.65rem]',
          className,
        )}
        style={{
          color: meta.color,
          backgroundColor: `color-mix(in srgb, ${meta.color} 15%, transparent)`,
        }}
      >
        {meta.label}
      </span>
    );
  }

  const primaryTone = meta.primary ? TONE_CLASS[ACTION_META[meta.primary].tone] : '';

  return (
    // Outer wrapper positions the (unclipped) dropdown; the inner bar clips its
    // segments so no square button background bleeds past the rounded corners.
    <div className={cn('relative inline-flex', lg && 'w-full', className)}>
      <div
        className={cn(
          'inline-flex items-stretch overflow-hidden rounded-md border border-hairline',
          lg && 'w-full',
        )}
      >
        {/* Current state — non-clickable badge. */}
        <span
          className={cn(
            'flex items-center font-semibold uppercase tracking-wide',
            lg ? 'px-4 py-2.5 text-xs' : 'px-2.5 py-1.5 text-[0.65rem]',
          )}
          style={{
            color: meta.color,
            backgroundColor: `color-mix(in srgb, ${meta.color} 15%, transparent)`,
          }}
        >
          {meta.label}
        </span>

        {meta.primary && (
          <button
            onClick={() => onAction(meta.primary!)}
            disabled={pending}
            className={cn(
              'flex items-center justify-center gap-1 border-l border-hairline font-semibold transition-colors disabled:opacity-50',
              pad,
              primaryTone,
              lg && 'flex-1',
            )}
          >
            {ACTION_META[meta.primary].label}
          </button>
        )}

        {meta.primary && meta.menu.length > 0 && (
          <button
            onClick={() => setOpen((o) => !o)}
            disabled={pending}
            aria-label="Other transitions"
            className={cn(
              'flex items-center border-l border-black/15 transition-colors disabled:opacity-50',
              lg ? 'px-2.5 text-sm' : 'px-2 text-xs',
              primaryTone,
            )}
          >
            ▾
          </button>
        )}
      </div>

      {open && meta.menu.length > 0 && (
        <>
          <button
            className="fixed inset-0 z-10 cursor-default"
            aria-hidden
            tabIndex={-1}
            onClick={() => setOpen(false)}
          />
          <div
            className={cn(
              'absolute right-0 z-20 min-w-[150px] rounded-md border border-hairline bg-surface py-1 shadow-xl',
              menuPlacement === 'top' ? 'bottom-full mb-1' : 'top-full mt-1',
            )}
          >
            {meta.menu.map((a) => (
              <button
                key={a}
                onClick={() => {
                  setOpen(false);
                  onAction(a);
                }}
                className={cn(
                  'block w-full px-3 py-1.5 text-left text-sm transition-colors hover:bg-surface-2',
                  a === 'reject' ? 'text-danger' : 'text-bone',
                )}
              >
                {ACTION_META[a].menuLabel}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
